import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Cloud,
  CheckCircle2,
  Link2Off,
  Loader2,
  KeyRound,
  Clock,
  ShieldCheck,
  Pencil,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { OrgEditDialog } from "@/components/OrgEditDialog";
import { useAuth } from "@/context/AuthContext";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  Legend,
  CartesianGrid,
  XAxis,
  YAxis,
  Cell,
  Tooltip,
} from "recharts";
import { SYSTEM_LABELS } from "@/lib/connectionConfig";
import { SfOrgSwitcher } from "@/components/SfOrgSwitcher";
import { ErrorState, GuideBanner } from "@/components/PageState";

const TREND_COLORS = ["#1d4ed8", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6"];

function shortDate(d) {
  try {
    return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return d;
  }
}

function TrendCard({ title, data }) {
  if (!data || !data.points || !data.points.length || !data.labels.length) {
    return (
      <div className="rounded-2xl border border-border bg-card p-5">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{title}</p>
        <p className="text-sm text-muted-foreground">No trend data yet.</p>
      </div>
    );
  }
  const last = data.points[data.points.length - 1];
  const labels = [...data.labels].sort((a, b) => (last[b] || 0) - (last[a] || 0)).slice(0, 5);
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={`trend-card-${title}`}>
      <p className="mb-4 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{title}</p>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={data.points} margin={{ top: 6, right: 20, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} minTickGap={24} />
          <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} width={48} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
          <Tooltip
            contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }}
            labelFormatter={shortDate}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {labels.map((l, i) => (
            <Line key={l} type="monotone" dataKey={l} stroke={TREND_COLORS[i % TREND_COLORS.length]} strokeWidth={2} dot={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function TrendsPanel({ system }) {
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api
      .get(`/connections/${system}/governance/trends`)
      .then((r) => setTrends(r.data))
      .catch(() => setTrends(null))
      .finally(() => setLoading(false));
  }, [system]);
  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  return (
    <div className="space-y-5" data-testid="trends-panel">
      <p className="text-xs text-muted-foreground">Daily values over the last 30 days.</p>
      <TrendCard title="Data & file storage" data={trends?.storage} />
      <TrendCard title="Automation & API usage" data={trends?.automation} />
      <TrendCard title="Licenses" data={trends?.licenses} />
    </div>
  );
}

const CHART_SECTIONS = new Set(["Storage", "API Limits", "Automation (Apex)", "Automation"]);

function chartFill(pct) {
  if (pct >= 90) return "#ef4444";
  if (pct >= 75) return "#f59e0b";
  return "#1d4ed8";
}

function GovTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-lg">
      <p className="font-medium">{d.name}</p>
      <p className="text-muted-foreground">
        {Number(d.used).toLocaleString()} / {Number(d.limit).toLocaleString()} {d.unit} ({d.pct}%)
      </p>
    </div>
  );
}

function GovernanceChart({ items }) {
  const data = items
    .filter((i) => i.type === "meter")
    .map((i) => ({
      name: i.label,
      pct: Math.min(100, Math.round(i.pct || 0)),
      used: i.used,
      limit: i.limit,
      unit: i.unit,
    }));
  if (!data.length) return null;
  return (
    <div className="mb-5 -ml-2" data-testid="gov-chart">
      <ResponsiveContainer width="100%" height={Math.max(120, data.length * 44)}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 44, bottom: 4, left: 8 }} barSize={16}>
          <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
          <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
          <Tooltip cursor={{ fill: "rgba(120,120,120,0.08)" }} content={<GovTooltip />} />
          <Bar dataKey="pct" radius={[0, 4, 4, 0]} label={{ position: "right", formatter: (v) => `${v}%`, fontSize: 11, fill: "hsl(var(--muted-foreground))" }}>
            {data.map((d, i) => (
              <Cell key={i} fill={chartFill(d.pct)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "Never";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function barColor(pct) {
  if (pct >= 90) return "bg-red-500";
  if (pct >= 75) return "bg-amber-500";
  return "bg-primary";
}

function MeterRow({ item }) {
  const pct = Math.min(100, item.pct || 0);
  return (
    <div data-testid={`gov-meter-${item.label}`}>
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-muted-foreground">{item.label}</span>
        <span className="font-medium">
          {Number(item.used).toLocaleString()}
          <span className="text-muted-foreground"> / {Number(item.limit).toLocaleString()} {item.unit}</span>
        </span>
      </div>
      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-muted">
        <div className={`h-full rounded-full transition-all ${barColor(item.pct)}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function TextRow({ item }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm" data-testid={`gov-text-${item.label}`}>
      <span className="text-muted-foreground">{item.label}</span>
      <span className="text-right font-medium">{item.value}</span>
    </div>
  );
}

export default function ConnectionDetail() {
  const { system } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const restricted = !!user?.restricted;
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const label = SYSTEM_LABELS[system] || system;

  const load = async () => {
    try {
      setLoadError("");
      const { data } = await api.get(`/connections/${system}/governance`);
      setData(data);
    } catch (e) {
      if (e.response?.status === 404) {
        toast.error("Connection not found");
        navigate("/dashboard/connections");
      } else {
        setData(null);
        setLoadError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load this connection.");
      }
    } finally {
      setLoading(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/connections/${system}/governance/sync`);
      setData(data);
      if (data.sync_error) {
        toast.error(`Sync issue: ${data.sync_error}`);
      } else {
        toast.success(data.source === "live" ? "Synced live from the org" : "Refreshed");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [system]);

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.delete(`/connections/${system}/org`);
      toast.success("Disconnected");
      navigate("/dashboard/connections");
    } catch (e) {
      toast.error("Failed to disconnect");
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) {
    return (
      <ErrorState
        title="Connection details unavailable"
        description={loadError || "Try again in a moment."}
        onRetry={load}
        actionTo="/dashboard/connections"
        actionLabel="Back to connections"
        testid="connection-error"
      />
    );
  }

  const connected = data.connected;

  return (
    <div data-testid="connection-detail-page">
      <Link
        to="/dashboard/connections"
        className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        data-testid="connection-detail-back"
      >
        <ArrowLeft className="h-4 w-4" /> Back to connections
      </Link>

      {system === "salesforce" && (
        <div className="mb-4">
          <SfOrgSwitcher onChanged={() => load()} className="w-fit rounded-lg border border-border bg-card px-3 py-2" />
        </div>
      )}

      {/* Header */}
      <div className="mb-8 rounded-2xl border border-border bg-card p-6" data-testid="connection-detail-header">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Cloud className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-display text-2xl font-bold tracking-tight lg:text-3xl">{label}</h1>
                <Badge variant={connected ? "default" : "secondary"} className="gap-1" data-testid="connection-detail-status">
                  {connected ? <CheckCircle2 className="h-3 w-3" /> : <Link2Off className="h-3 w-3" />}
                  {connected ? "Connected" : "Disconnected"}
                </Badge>
              </div>
              {data.label && <p className="mt-0.5 text-sm text-muted-foreground">{data.label}</p>}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Last sync</p>
                <p className="font-medium" data-testid="connection-detail-last-sync">{fmtDate(data.last_sync_at)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <KeyRound className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Auth status</p>
                <p className="font-medium capitalize" data-testid="connection-detail-auth">
                  {data.auth_status.replace("_", " ")}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button className="gap-2" onClick={syncNow} disabled={syncing || restricted} data-testid="connection-sync-now">
                <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> Sync now
              </Button>
              <Button variant="outline" className="gap-2" onClick={() => setEditOpen(true)} disabled={restricted} data-testid="connection-edit-credentials">
                <Pencil className="h-4 w-4" /> Edit credentials
              </Button>
              <Button
                variant="outline"
                className="gap-2 text-destructive hover:text-destructive"
                onClick={disconnect}
                disabled={busy || restricted}
                data-testid="connection-detail-disconnect"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2Off className="h-4 w-4" />} Disconnect
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Governance details / Trends */}
      <div className="mb-4 flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-primary" />
        <h2 className="font-display text-lg font-bold">Governance details</h2>
        <Badge variant={data.source === "live" ? "default" : "secondary"} className="gap-1" data-testid="governance-source">
          {data.source === "live" ? "Live org" : data.source === "mock" ? "Sample data" : "Not synced"}
        </Badge>
        {data.synced_at && (
          <span className="ml-auto text-xs text-muted-foreground" data-testid="governance-synced-at">
            Last synced {fmtDate(data.synced_at)}
          </span>
        )}
      </div>

      {data.source === "mock" && !data.sync_error && (
        <GuideBanner tone="warn" testid="governance-sample-banner">
          These meters are <strong>sample data</strong> so you can explore the layout. Salesforce live orgs show real limits after Sync now.
        </GuideBanner>
      )}
      {data.sync_error && (
        <div
          className="mb-4 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200"
          data-testid="governance-sync-error"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Couldn't sync live from the org ({data.sync_error}). Showing
            {data.source === "live" ? " the last successful sync." : " sample data until the connection succeeds."}
          </span>
        </div>
      )}

      <Tabs defaultValue="details" className="w-full">
        <TabsList data-testid="governance-tabs">
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="trends" data-testid="tab-trends">Trends</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-4">
          {data.sections.length === 0 ? (
            <p className="text-sm text-muted-foreground">Connect this system to view governance details.</p>
          ) : (
            <Accordion
              type="multiple"
              defaultValue={data.sections.map((s) => s.title)}
              className="rounded-2xl border border-border bg-card px-5"
              data-testid="governance-sections"
            >
              {data.sections.map((section) => (
                <AccordionItem
                  key={section.title}
                  value={section.title}
                  data-testid={`gov-section-${section.title}`}
                  className="last:border-b-0"
                >
                  <AccordionTrigger className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground hover:no-underline">
                    <span className="flex items-center gap-2">
                      {section.title}
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium normal-case tracking-normal text-muted-foreground">
                        {section.items.length}
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    {CHART_SECTIONS.has(section.title) && <GovernanceChart items={section.items} />}
                    <div className="space-y-3.5">
                      {section.items.map((item, i) =>
                        item.type === "meter" ? <MeterRow key={i} item={item} /> : <TextRow key={i} item={item} />
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </TabsContent>

        <TabsContent value="trends" className="mt-4">
          <TrendsPanel system={system} />
        </TabsContent>
      </Tabs>

      <OrgEditDialog
        system={system}
        open={editOpen}
        onOpenChange={setEditOpen}
        existingOrg={{ label: data.label, meta: data.org_meta }}
        mode="edit"
        onSaved={load}
      />
    </div>
  );
}
